import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import RemoveIcon from '@mui/icons-material/Remove'
import AddIcon from '@mui/icons-material/Add'
import { useTranslation } from 'react-i18next'

// A number you set, not a number you type into a 48px box.
//
// It replaces the `width: '3rem'` TextField on the multiple-quantity prompt, whose
// emptied value reached handleSetQuantity as '' and was posted as parseInt('') -> NaN.
//
// The value is a STRING, deliberately. A stepper whose field cannot be empty cannot be
// edited by typing, and one that coerces '' to 0 silently answers a question the clerk
// has not answered yet. Emptiness is a real state; isValidQuantity is how a caller
// refuses to submit it.

const MIN_TARGET = 44

export function isValidQuantity(value) {
    return /^\d+$/.test(String(value ?? '').trim())
}

export default function QuantityStepper(props) {
    const { value, onChange, min = 0, max = 999, disabled } = props
    const { t } = useTranslation()

    const numeric = isValidQuantity(value) ? parseInt(value, 10) : null

    // An empty field steps to the floor rather than to NaN.
    const step = delta => {
        const from = numeric == null ? min : numeric + delta
        onChange(String(Math.min(max, Math.max(min, from))))
    }

    const handleType = event => {
        const next = event.target.value
        if (next === '') return onChange('')
        if (!/^\d+$/.test(next)) return
        if (parseInt(next, 10) > max) return
        onChange(next)
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
                aria-label={t('common.decrease')}
                onClick={() => step(-1)}
                disabled={disabled || (numeric != null && numeric <= min)}
                sx={{ width: MIN_TARGET, height: MIN_TARGET }}
            >
                <RemoveIcon />
            </IconButton>

            <TextField
                value={value}
                onChange={handleType}
                disabled={disabled}
                inputProps={{
                    inputMode: 'numeric',
                    'aria-label': t('common.quantity'),
                    style: { textAlign: 'center', fontSize: '1.3125rem' }
                }}
                sx={{ width: '5.5rem' }}
            />

            <IconButton
                aria-label={t('common.increase')}
                onClick={() => step(1)}
                disabled={disabled || (numeric != null && numeric >= max)}
                sx={{ width: MIN_TARGET, height: MIN_TARGET }}
            >
                <AddIcon />
            </IconButton>
        </Box>
    )
}
